import type { Build, Issue } from './types';

/**
 * CUTTING A RELEASE — WHAT WENT OUT, AND WHAT IT IS CALLED.
 *
 * Built 15 August 2026, on Vinnie's choice of *"B then A"* from the two options
 * in the mockups: a release is its own act, and closing a sprint later offers to
 * perform that act with the boxes already filled.
 *
 * THE ORDER WAS THE DECISION, more than the shape. Built the other way round —
 * a release as a thing that only happens when a sprint ends — the assumption of
 * one release per fortnight gets written into the data, and taking it out
 * afterwards means revisiting every release already recorded. This way a sprint
 * is one occasion for cutting a release and never the only one, so a hot-fix on
 * a Tuesday, two releases in one fortnight, and a board not using sprints at all
 * are all ordinary rather than special.
 *
 * WHO DECIDES THE VERSION, which is the part with a rule attached. Vinnie,
 * 15 August: *"when the board is controlled by itself, the versioning is
 * controlled by the developer and he/she should have input on that... if the
 * switch is on, then it is driven by the assistant during development."*
 *
 * So there are two worlds and one switch between them:
 *
 *   SWITCH OFF   a person types the version. The software SUGGESTS the next
 *                patch, minor and major, and applies none of them by itself.
 *   SWITCH ON    the version and the build number arrive with the push, from
 *                software that already has a version of its own. FlightDeck
 *                records what it is told rather than deciding it, because a
 *                second opinion about a version number is two numbers that will
 *                eventually disagree.
 *
 * Nothing here reads the switch — the screens do. This file answers the
 * questions both worlds ask: what is the next version, what has shipped since
 * the last one, and is this draft fit to record.
 */

/** A release being written, before it is recorded. */
export interface Draft {
  version: string;
  date: string; // YYYY-MM-DD
  /** Issue refs, e.g. ["#158", "#196"] — what a reader is being told went out. */
  items: string[];
  /** One sentence a person would say. Optional; most releases do not need one. */
  plain?: string;
  /** The sprint this was cut at the close of, when it was. */
  sprint?: string;
  /**
   * What it was installed on, comma-separated. Only ever set on a board whose
   * project tracks devices — everywhere else this stays absent, and absent is
   * not the same as empty: see deviceLine.
   */
  installed?: string;
}

/* ── Version numbers ───────────────────────────────────────────────────────── */

/**
 * THE THREE SUGGESTIONS, and they are suggestions rather than a decision.
 *
 * Offered beside the box, never typed into it. A team that numbers its releases
 * by date, or by a scheme nobody outside would guess, types what it types and
 * nothing here argues — `undefined` back means "no idea what comes next", and
 * the screen simply shows no buttons rather than inventing three.
 *
 * MAJOR.MINOR.PATCH IS RECOGNISED, WITH ANYTHING TRAILING KEPT OUT OF THE WAY.
 * `1.42.0+262` is a version and a build stuck together, which is what mobile
 * tooling produces; the suffix belongs to the build number, so it is dropped
 * from the suggestions rather than incremented into nonsense like `1.42.1+262`.
 */
export function nextVersions(last: string | undefined): { patch: string; minor: string; major: string } | undefined {
  const m = /^(\d+)\.(\d+)\.(\d+)/.exec((last ?? '').trim().replace(/^v/i, ''));
  if (!m) return undefined;
  const [major, minor, patch] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return {
    patch: `${major}.${minor}.${patch + 1}`,
    minor: `${major}.${minor + 1}.0`,
    major: `${major + 1}.0.0`,
  };
}

/**
 * The release before this one, by build number.
 *
 * BY BUILD NUMBER RATHER THAN BY DATE, because dates are the field most likely
 * to be missing or wrong — several of PhotoSyncApp's were reconstructed
 * afterwards and are marked as inferred. The build number is the one thing that
 * has always counted upwards.
 */
export function lastRelease(builds: Build[]): Build | undefined {
  return builds.length ? [...builds].sort((a, b) => b.build - a.build)[0] : undefined;
}

/** The next build number — one more than the highest, or 1 on an empty board. */
export function nextBuildNumber(builds: Build[]): number {
  const last = lastRelease(builds);
  return last ? last.build + 1 : 1;
}

/* ── What has shipped since ────────────────────────────────────────────────── */

/** Work that is finished and so could be in a release. */
const isShippable = (i: Issue) => i.status === 'done';

/**
 * WHAT TO OFFER: everything finished that no release has claimed yet.
 *
 * BY WHAT IS ALREADY RECORDED, not by date. "Everything finished since the 2nd
 * of August" sounds equivalent and is not: it misses work finished before the
 * last release but left out of it deliberately, and it double-counts anything
 * whose status was corrected afterwards. Asking "which refs do the existing
 * releases already list" is the question that actually means what the sentence
 * says, and it is stable when somebody edits a date.
 *
 * WON'T-DO WORK IS NOT IN A RELEASE. It is resolved, not shipped, and a
 * changelog listing things nobody built is a changelog that gets ignored.
 *
 * NOTHING IS TICKED FOR THE PERSON. The list comes back with everything
 * offered; the screen decides what is ticked, and the person decides what
 * stays ticked. Internal work that nobody outside would recognise is exactly
 * what should be untickable, and that is a judgement no rule here can make.
 */
export function notYetReleased(issues: Issue[], builds: Build[]): Issue[] {
  /*
   * A PULLED RELEASE CLAIMS NOTHING, and this is the mirror of the rule above
   * rather than an exception to it.
   *
   * The rule above exists so work is not put in two changelogs. The fault in
   * the other direction is worse and quieter: 1.5.0 carried #196 and was
   * withdrawn, so #196 IS NOT OUT THERE — and if the withdrawn build keeps its
   * claim, #196 never appears in another release's list for as long as the
   * board exists. Nobody would notice. The fix that made it into a build
   * nobody can install would be recorded as shipped forever.
   *
   * So the work comes back, and the next real release carries it.
   */
  const claimed = new Set(builds.filter((b) => !b.pulled).flatMap((b) => b.items ?? []));
  return issues.filter((i) => isShippable(i) && !claimed.has(i.ref));
}

/**
 * The same question asked of one sprint — what it finished that has not gone
 * out. This is the whole of Option A: closing a sprint offers a release, and
 * this is the list it offers.
 *
 * WORK CARRIED OVER IS ABSENT because it is not finished, and work finished in
 * an earlier sprint but never released IS absent too — it did not ship in this
 * fortnight, and quietly folding it in makes the sprint look more productive
 * than it was. Whoever wants it can tick it back on.
 */
export function finishedInSprint(issues: Issue[], builds: Build[], sprintId: string): Issue[] {
  return notYetReleased(issues, builds).filter((i) => (i.sprint ?? '') === sprintId);
}

/* ── Is it fit to record ───────────────────────────────────────────────────── */

/**
 * WHAT STOPS A RELEASE BEING RECORDED, in the words to show the person.
 *
 * An empty string means nothing is wrong. The same shape as `sprintProblem` in
 * sprints.ts, deliberately: one convention for "why the button did nothing".
 */
export function releaseProblem(draft: Draft, builds: Build[]): string {
  const version = draft.version.trim();
  if (!version) return 'Give the release a version — whatever you actually shipped.';
  if (version.length > 40) return 'That version is too long to sit on a card — keep it under 40 characters.';
  /*
   * TWO RELEASES CANNOT SHARE A VERSION, and this is the one worth defending.
   * A changelog with 1.5.0 in it twice cannot answer "what is in 1.5.0", and
   * the answer somebody needs is usually being looked up because something has
   * gone wrong in the field.
   */
  const clash = builds.find((b) => b.version.trim().toLowerCase() === version.toLowerCase());
  if (clash) return `${clash.version} has already gone out — build ${clash.build}, ${clash.date || 'date not recorded'}.`;
  if (!draft.date) return 'A release needs the date it went out.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.date)) return 'That date is not a real date.';
  /*
   * A RELEASE WITH NOTHING IN IT IS ALLOWED, and it is worth saying why rather
   * than leaving it to look like an oversight. Rebuilds happen — a certificate
   * expires, a store rejects a binary for its own reasons — and the honest
   * record is a version that went out carrying no changes. Refusing it would
   * teach people to invent an item.
   */
  return '';
}

/** A draft ready to be written, with the build number filled in. */
export function toBuild(draft: Draft, builds: Build[]): Omit<Build, 'id'> & { sprint?: string } {
  return {
    build: nextBuildNumber(builds),
    version: draft.version.trim(),
    date: draft.date,
    items: draft.items,
    plain: (draft.plain ?? '').trim(),
    /*
     * TYPED BY A PERSON HERE, WHICH IS WHY NONE OF IT IS `installedTold`.
     *
     * This was hardcoded to '' — not as a placeholder but as a dead end, so
     * every release cut by hand said "Phones not recorded" for ever and there
     * was no way to make it say anything else. That is how Vinnie found it:
     * *"every release version says 'Phones not recorded'."*
     */
    installed: deviceText(deviceList(draft.installed)),
    /*
     * RECORDED BY A PERSON, AT THE TIME — which is the high-confidence case.
     * `confidence` exists because PhotoSyncApp's older builds were
     * reconstructed from git afterwards and are honestly marked as guesses.
     * Anything cut through this screen is somebody saying so on the day.
     */
    confidence: 'high',
    ...(draft.sprint ? { sprint: draft.sprint } : {}),
  };
}

/**
 * The sentence describing where a board's versions come from.
 *
 * Written here rather than in the screen because two screens ask it — Releases,
 * and the sprint-closing dialog — and the same fact worded two ways reads as two
 * different rules.
 */
/* ── Taking one back ───────────────────────────────────────────────────────── */

/** What is being recorded about a release that should no longer be running. */
export interface Pull {
  kind: 'withdrawn' | 'rolledback';
  why: string;
  /** The version put back in its place. Only meaningful for a rollback. */
  back?: string;
}

/** The word for it, wherever it is shown. One spelling, two screens. */
export const PULLED_LABEL: Record<'withdrawn' | 'rolledback', string> = {
  withdrawn: 'Withdrawn',
  rolledback: 'Rolled back',
};

/**
 * WHAT STOPS A RELEASE BEING PULLED.
 *
 * THE REASON IS REQUIRED, and it is the only thing here that is. A card saying
 * "1.5.0 — withdrawn" and nothing else sends the next person asking around,
 * which is the entire cost this feature exists to remove. It is also the field
 * somebody will most want to skip, because at the moment a release is pulled
 * everybody involved already knows why — and in six months nobody does.
 */
export function pullProblem(pull: Pull, build: Build | undefined, builds: Build[]): string {
  if (!build) return 'That release is not on this board any more.';
  if (build.pulled) return `Build ${build.build} is already marked ${PULLED_LABEL[build.pulled].toLowerCase()}.`;
  const why = pull.why.trim();
  if (!why) return 'Say why it was taken back — in six months this is the only record of it.';
  if (why.length > 200) return 'Keep the reason to a line or two — under 200 characters.';
  const back = (pull.back ?? '').trim();
  if (back) {
    /*
     * PUT BACK TO SOMETHING THAT EXISTS. A rollback naming a version this board
     * has never heard of is either a typo or a release nobody recorded, and both
     * are worth stopping at the moment somebody can still say which.
     */
    const known = builds.some((b) => b.version.trim().toLowerCase() === back.toLowerCase());
    if (!known) return `No release called ${back} was ever recorded here — check the version.`;
    if (back.toLowerCase() === build.version.trim().toLowerCase())
      return 'A release cannot be rolled back to itself.';
  }
  return '';
}

/** What to write on the build when it is pulled. */
export function toPulled(pull: Pull, who: string, at: number): Partial<Build> {
  const back = (pull.back ?? '').trim();
  return {
    pulled: pull.kind,
    pulledWhy: pull.why.trim(),
    pulledBy: who,
    pulledAt: at,
    // Only a rollback puts something back; carrying `back` on a withdrawal
    // would render a card that claims a version is running when none is.
    pulledBack: pull.kind === 'rolledback' ? back : '',
  };
}

/**
 * The versions a rollback could name, newest first.
 *
 * EARLIER BUILDS ONLY, and not ones that were themselves pulled — rolling back
 * to a version that was also withdrawn is not a recovery, and offering it in a
 * list reads as an endorsement.
 */
export function couldRollBackTo(build: Build, builds: Build[]): string[] {
  return builds
    .filter((b) => b.build < build.build && !b.pulled && b.version.trim())
    .sort((a, b) => b.build - a.build)
    .map((b) => b.version.trim());
}

/**
 * WORK BEING OFFERED AGAIN BECAUSE THE RELEASE CARRYING IT WAS TAKEN BACK.
 *
 * THE RULE WORKED AND NOBODY COULD SEE IT. Vinnie, testing on 15 August, pulled
 * a release and then reported *"I am not seeing it in the list when I click cut
 * a release."* It was in the list. PhotoSyncApp offers 138 rows, and finding the
 * two that had just come back means remembering which refs they were and
 * scanning for them.
 *
 * A RULE NOBODY CAN OBSERVE IS INDISTINGUISHABLE FROM A BROKEN ONE, and the
 * person about to cut the REPLACEMENT release is exactly who needs telling: this
 * work has not reached anybody, whatever the last changelog said.
 */
export function returnedByPull(
  offered: Pick<Issue, 'ref'>[],
  builds: Build[]
): Map<string, Build> {
  const back = new Map<string, Build>();
  const here = new Set(offered.map((i) => i.ref));
  for (const b of builds) {
    if (!b.pulled) continue;
    for (const ref of b.items ?? []) if (here.has(ref) && !back.has(ref)) back.set(ref, b);
  }
  return back;
}

/**
 * One sentence saying what happened to a pulled release, for the card.
 *
 * WHAT IS RUNNING INSTEAD IS THE POINT of saying it at all — the reader is
 * usually somebody working out what is on people's phones right now.
 */
export function pulledReading(build: Build): string {
  if (!build.pulled) return '';
  const when = build.pulledAt ? new Date(build.pulledAt).toISOString().slice(0, 10) : '';
  const what =
    build.pulled === 'rolledback'
      ? build.pulledBack
        ? `Rolled back to ${build.pulledBack}`
        : 'Rolled back'
      : 'Withdrawn';
  return when ? `${what} on ${when}` : what;
}

export function whoNumbersIt(assistantVersions: boolean, last: Build | undefined): string {
  if (assistantVersions) {
    return last
      ? `Versions on this board come from the tool that pushes to it — the last was ${last.version || `build ${last.build}`}. FlightDeck records what it is told rather than deciding it.`
      : 'Versions on this board come from the tool that pushes to it. Nothing has been pushed yet.';
  }
  if (!last) return 'This will be the first release recorded on this board.';
  /*
   * IF THE LAST ONE WAS TAKEN BACK, SAY SO HERE. Somebody cutting the release
   * that REPLACES a withdrawn one is the likeliest person to be at this box,
   * and "the last release was 1.5.0" without that word reads as though 1.5.0 is
   * out there working.
   */
  const name = last.version || `build ${last.build}`;
  if (last.pulled)
    return `The last release was ${name}, and it was ${PULLED_LABEL[last.pulled].toLowerCase()}. Its number stays used — the work it carried is offered again below.`;
  return `The last release was ${name}${last.date ? `, on ${last.date}` : ''}.`;
}

/* ── What it was installed on ──────────────────────────────────────────────── */

/**
 * DEVICES, ON THE BOARDS WHERE THERE ARE ANY.
 *
 * Vinnie, 15 August 2026, having read the same four words under every release he
 * has ever cut: *"every release version says 'Phones not recorded'. This app is
 * not only meant for phones, but for desktop software as well… But if I am
 * developing a SaaS then there will be no recorded device… I want the best of
 * both worlds, able to tag devices as well remove them."*
 *
 * THE FIRST QUESTION IS WHETHER THERE IS A QUESTION. A web product is released,
 * not installed. Nothing carries it. Saying its devices were "not recorded" is
 * an accusation of sloppiness about a fact that does not exist, and it was
 * printed on every release on every such board. So `tracksDevices` is off by
 * default and the line is not drawn at all — see deviceLine, which has three
 * answers where the screen used to have two.
 *
 * THE LIST HAS TWO AUTHORS, and this is the part that governs everything below.
 * A tool knows what it pushed at the moment it pushed. A person knows the Fold
 * was done by cable two days later and the S26 updated itself over the air fifty
 * minutes after publication — both of which happened on this board, and neither
 * of which any tool witnessed. So the list keeps growing after the release goes
 * out, and a push that replaced it would silently delete the true half.
 *
 * THE OWNERSHIP RULE, once, here, because three places depend on it:
 *
 *   the tool owns what it reports    — `installedTold`
 *   a person owns what they added    — everything else in `installed`
 *   neither erases the other
 *
 * Which settles the two removals in opposite and correct directions. Take out a
 * device you typed and it stays out. Take out one the tool reported and the next
 * push puts it back — because the ledger still says so, and the ledger is where
 * that correction belongs. Same rule the tracker already follows for a title or
 * a status: what the document owns, the board does not overrule.
 */

/** Split a written list into names. Blank, spacing and stray commas all vanish. */
export function deviceList(text: string | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of (text ?? '').split(',')) {
    const name = raw.trim().replace(/\s+/g, ' ');
    if (!name) continue;
    // Case-folded for sameness only. "Pixel" and "pixel" are one device, and
    // the spelling kept is the one written first rather than the tidier one —
    // renaming somebody's device while merging is not this function's business.
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/** How a list is stored and shown. One place, so both halves cannot drift. */
export function deviceText(devices: string[]): string {
  return devices.join(', ');
}

/**
 * EVERY DEVICE THIS PROJECT HAS ALREADY SHIPPED TO, likeliest first.
 *
 * This is the whole of *"a list with a custom option"* — and the list is the
 * project's own history rather than anything built in. A menu of generic device
 * names would offer "Android phone" to somebody whose four devices are called
 * S25, S26, Pixel and Fold, which is a menu nobody can use. Type them once and
 * they are offered ever after.
 *
 * Ordered by how often each has carried a release, then by the newest build it
 * appeared on. A device retired last year sinks below the ones in use, without
 * anybody having to retire it by hand.
 */
export function knownDevices(builds: Build[]): string[] {
  const count = new Map<string, { name: string; used: number; newest: number }>();
  for (const b of builds) {
    for (const name of deviceList(b.installed)) {
      const key = name.toLowerCase();
      const had = count.get(key);
      if (had) {
        had.used += 1;
        had.newest = Math.max(had.newest, b.build);
      } else {
        count.set(key, { name, used: 1, newest: b.build });
      }
    }
  }
  return [...count.values()]
    .sort((a, b) => b.used - a.used || b.newest - a.newest || a.name.localeCompare(b.name))
    .map((d) => d.name);
}

/**
 * WHAT A PUSH SHOULD LEAVE BEHIND, given what the board holds and what the tool
 * now says. The ownership rule above, made arithmetic.
 *
 * Every build that predates this has no `installedTold`, so its devices count as
 * a person's and are kept whatever the tool says — which is the right way for
 * this to arrive on a board with a hundred and fifty builds already on it.
 */
export function mergeDevices(
  build: Pick<Build, 'installed' | 'installedTold'>,
  told: string | undefined
): { installed: string; installedTold: string } {
  const now = deviceList(told);
  const nowKeys = new Set(now.map((d) => d.toLowerCase()));
  const toldBefore = new Set(deviceList(build.installedTold).map((d) => d.toLowerCase()));
  // A person's half: everything on the board the tool never claimed.
  const hand = deviceList(build.installed).filter((d) => !toldBefore.has(d.toLowerCase()));
  const kept = hand.filter((d) => !nowKeys.has(d.toLowerCase()));
  return { installed: deviceText([...now, ...kept]), installedTold: deviceText(now) };
}

/**
 * THE THREE ANSWERS THE RELEASE CARD NEEDS, where it used to have two.
 *
 * `off` is the one that was missing, and it is not "empty" — it is the absence
 * of the question. A SaaS board draws no line here at all, which is the fix
 * Vinnie asked for; `empty` still says nothing was written down, because on a
 * board that does ship to devices that remains worth saying.
 */
export type DeviceLine =
  | { kind: 'off' }
  | { kind: 'empty' }
  | { kind: 'some'; devices: { name: string; told: boolean }[] };

export function deviceLine(
  build: Pick<Build, 'installed' | 'installedTold'>,
  tracksDevices: boolean
): DeviceLine {
  if (!tracksDevices) return { kind: 'off' };
  const told = new Set(deviceList(build.installedTold).map((d) => d.toLowerCase()));
  const devices = deviceList(build.installed).map((name) => ({
    name,
    told: told.has(name.toLowerCase()),
  }));
  return devices.length ? { kind: 'some', devices } : { kind: 'empty' };
}

/**
 * THE ONE LINE UNDER THE VERSION BOX, once the panel stopped explaining itself
 * in paragraphs.
 *
 * Vinnie, 15 August, on the recording form: *"it looks a bit busy and there is
 * a lot of text, whilst the text is important, it is overwhelming."* He was
 * right — there were six standing explanations on that screen, shown in full,
 * every time. Most became an `i` dot. This one could not: what the last release
 * was, and what this one will be numbered, is the answer to a question somebody
 * is holding while they type, not background reading.
 *
 * SO IT IS SHORTER, EXCEPT WHEN IT MUST NOT BE. If the last release was taken
 * back, the long sentence stays word for word — see whoNumbersIt. Somebody
 * cutting the replacement for a withdrawn release is the likeliest person at
 * this box, and trimming *"and it was withdrawn"* to save a line would remove
 * the only warning that 1.5.0 is not out there working. Brevity is not worth a
 * fact, and knowing which sentences are load-bearing is the whole job.
 */
export function buildLine(last: Build | undefined, next: number): string {
  if (!last) return `This will be build ${next} — the first recorded on this board.`;
  if (last.pulled) return `${whoNumbersIt(false, last)} This will be build ${next}.`;
  const name = last.version || `build ${last.build}`;
  return `Build ${next}, after ${name}${last.date ? ` on ${last.date}` : ''}.`;
}

/**
 * WHY WORK IS BEING OFFERED AGAIN, in a few words rather than a paragraph.
 *
 * The paragraph it replaces said the same thing at four times the length and
 * sat above the list every time a release had ever been taken back. The full
 * reasoning moves behind an `i` dot; this is what stays on screen, because the
 * COUNT is the part that makes somebody look twice.
 */
export function backLine(back: Map<string, Build>): string {
  if (back.size === 0) return '';
  /*
   * DE-DUPLICATED BY NAME, NOT BY OBJECT.
   *
   * The paragraph this replaced did `new Set([...back.values()])`, which is a
   * set of REFERENCES: two objects describing the same release are two members,
   * and the line reads "1.2.0 and 1.2.0". It never showed, because
   * returnedByPull hands the same object to every ref of one release — so the
   * fault was invisible and entirely dependent on that staying true.
   */
  const from = [...new Set([...back.values()].map((b) => b.version || `build ${b.build}`))];
  return `${back.size} back from ${from.join(' and ')}`;
}

/**
 * WHERE A RELEASE HAS GOT TO.
 *
 *   prepared → awaiting sign-off → signed off → cut → gone live
 *
 * Vinnie, 21 August: *"I don't like how a release is cut then it asks for a
 * signoff, it needs to happen before a release is cut."* So declaring what is
 * in a release no longer cuts it — it prepares it, and cutting is what approval
 * earns. A release on the list is approved work by definition.
 *
 * ASKED IN ONE PLACE, because the answer has five parts and two screens
 * answering it separately is how they come to disagree.
 *
 * READ, NOT INFERRED, and that is the change. This used to be
 * `wentLiveAt != null || signoffRequestedAt == null` — live unless somebody was
 * asked to sign. That could not tell a release nobody signed from one nobody
 * has signed YET, which is exactly the difference "prepared" introduces.
 * Migration 0050 wrote the answer down for every release that already existed,
 * so nothing has to be guessed from an absence any more.
 */
export type ReleaseState = 'prepared' | 'awaiting' | 'rejected' | 'signed' | 'cut' | 'live';

export function releaseState(
  build: {
    wentLiveAt?: number | null;
    cutAt?: number | null;
    signoffRequestedAt?: number | null;
  },
  signatures: Answer[] = []
): ReleaseState {
  if (build.wentLiveAt != null) return 'live';
  if (build.cutAt != null) return 'cut';
  if (build.signoffRequestedAt == null) return 'prepared';
  /*
   * A REFUSAL IS ITS OWN STATE, and it has to be: everybody having answered is
   * not the same as everybody having agreed, so without this the last person
   * could reject it and the list would read "Signed off". The database refuses
   * the cut either way — 0052 — but a list that says the opposite of the record
   * is worse than one that stops you.
   */
  if (signatures.some((s) => s.answer === 'rejected')) return 'rejected';
  return signatures.length > 0 && signatures.every((s) => s.signedAt) ? 'signed' : 'awaiting';
}

/** What each state is called on screen. The stored answer is never shown. */
export const RELEASE_STATE: Record<ReleaseState, string> = {
  prepared: 'Not yet approved',
  awaiting: 'Awaiting sign-off',
  rejected: 'Rejected',
  signed: 'Signed off',
  cut: 'Cut',
  live: 'Live',
};

/**
 * MAY THIS BE CUT NOW?
 *
 * Nothing outstanding, which is one sentence covering both cases. A board that
 * names nobody as signing work off has nothing outstanding from the first
 * moment, so recording a release there still means it is out, in one act,
 * exactly as before — see 0050 for why any other reading would hand those
 * customers a ladder with no rungs.
 */
export const readyToCut = (signatures: Answer[]): boolean =>
  signatures.every((s) => s.signedAt != null) &&
  !signatures.some((s) => s.answer === 'rejected');

/** Only the two fields either question turns on, so a caller need not hold a
 *  whole Signoff to ask. */
type Answer = { signedAt?: number | null; answer?: 'approved' | 'rejected' };
